import { cn } from "@/lib/utils";

/** Landing marketing chat — square edges on bubbles and avatars. */
export const landingChatAvatarShellClass = "rounded-none overflow-hidden bg-transparent";

export const landingChatBubbleRadiusClass = "rounded-none";

export type TLandingBubbleSentTone = "light" | "teal";
export type TLandingBubbleReceivedTone = "agent" | "surface";

type TLandingBubbleStyleKey = `sent-${TLandingBubbleSentTone}` | `received-${TLandingBubbleReceivedTone}`;

/* Bubbles use the glassier `.landing-glass-bubble` recipe (styles.css): nested inside the
 frosted chat card, the card's `.landing-glass-surface` recipe would composite to flat white,
 so the bubble variant drops the background opacity to keep the frost visible. No drop shadow. */
const GLASS_SHELL_CLASS = "landing-glass-bubble";

const SHELL_CLASS: Partial<Record<TLandingBubbleStyleKey, string>> = {
	"sent-light": GLASS_SHELL_CLASS,
	"sent-teal":
		"bg-teal-500 text-white shadow-[0_10px_50px_color-mix(in_srgb,var(--color-black)_3%,transparent)] text-[18px] font-medium leading-[1.3] tracking-[-0.03em] max-md:text-[16px]",
	"received-agent":
		"bg-white shadow-[0_10px_50px_color-mix(in_srgb,var(--color-black)_3%,transparent)] text-[18px] font-medium leading-[1.3] tracking-[-0.03em] max-md:text-[16px]",
	"received-surface": cn("min-w-0 overflow-hidden", GLASS_SHELL_CLASS),
};

const TEXT_CLASS: Partial<Record<TLandingBubbleStyleKey, string>> = {
	"sent-light":
		"m-0 text-teal-500 font-sans text-[clamp(13px,1.15vw,16.5px)] font-medium leading-[1.35] tracking-[-0.02em] text-right whitespace-nowrap max-md:min-w-0 max-md:text-[12.5px] max-md:whitespace-normal",
	"sent-teal": "m-0",
	"received-agent": "m-0",
	"received-surface":
		"m-0 text-left font-sans text-[clamp(13px,1.15vw,16.5px)] font-medium leading-[1.32] tracking-[-0.01em] text-teal-500 max-md:text-[12.5px] max-md:leading-[1.38]",
};

const DEFAULT_PADDING: Partial<Record<TLandingBubbleStyleKey, string>> = {
	"sent-light": "px-5 py-3.5 max-md:px-4 max-md:py-3",
	"sent-teal": "px-[25px] py-[18px]",
	"received-agent": "px-[25px] py-[18px]",
	"received-surface": "px-5 py-3.5 max-md:px-4 max-md:py-3",
};

function styleKey(
	variant: "sent" | "received",
	tone: TLandingBubbleSentTone | TLandingBubbleReceivedTone,
): TLandingBubbleStyleKey {
	return `${variant}-${tone}` as TLandingBubbleStyleKey;
}

export function landingBubbleShellClass(variant: "sent", tone: TLandingBubbleSentTone): string;
export function landingBubbleShellClass(variant: "received", tone: TLandingBubbleReceivedTone): string;
export function landingBubbleShellClass(
	variant: "sent" | "received",
	tone: TLandingBubbleSentTone | TLandingBubbleReceivedTone,
) {
	return SHELL_CLASS[styleKey(variant, tone)] ?? "";
}

export function landingBubbleTextClass(variant: "sent", tone: TLandingBubbleSentTone): string;
export function landingBubbleTextClass(variant: "received", tone: TLandingBubbleReceivedTone): string;
export function landingBubbleTextClass(
	variant: "sent" | "received",
	tone: TLandingBubbleSentTone | TLandingBubbleReceivedTone,
) {
	return TEXT_CLASS[styleKey(variant, tone)] ?? "";
}

export function landingBubblePaddingClass(
	variant: "sent",
	tone: TLandingBubbleSentTone,
	paddingClassName?: string,
): string;
export function landingBubblePaddingClass(
	variant: "received",
	tone: TLandingBubbleReceivedTone,
	paddingClassName?: string,
): string;
export function landingBubblePaddingClass(
	variant: "sent" | "received",
	tone: TLandingBubbleSentTone | TLandingBubbleReceivedTone,
	paddingClassName?: string,
) {
	const key = styleKey(variant, tone);
	return cn(paddingClassName ?? DEFAULT_PADDING[key] ?? "");
}

type TLandingChatRole = "user" | "assistant";

export function landingChatBubbleShellClass(role: TLandingChatRole) {
	return role === "user" ? landingBubbleShellClass("sent", "light") : landingBubbleShellClass("received", "surface");
}

export function landingChatBubbleTextClass(role: TLandingChatRole) {
	return role === "user" ? landingBubbleTextClass("sent", "light") : landingBubbleTextClass("received", "surface");
}

export function landingChatBubblePaddingClass(role: TLandingChatRole, paddingClassName?: string) {
	return role === "user"
		? landingBubblePaddingClass("sent", "light", paddingClassName)
		: landingBubblePaddingClass("received", "surface", paddingClassName);
}
